import type { ProgramSeason } from '@/modules/Entertainment/interface/Program';
import { Fragment, type FC } from 'react';
import {
  Flex,
  Skeleton,
  SkeletonCircle,
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
} from '@eqi-investimentos/react';
import { Slider } from '@eqi-investimentos/slider';
import { Divider } from '@chakra-ui/react';
import { PATHS } from '@/config/paths';
import { useGetProgramSeasons } from '@/modules/Entertainment/http/client/useGetProgramSeasons';
import { ContentProgressCard } from '@/modules/Entertainment/ui/Widgets/ContentProgressCard';
import { formatDateToPTBRString } from '@/utils/date';
import { IfRender } from '@/utils/jsx';
import '@eqi-investimentos/slider/css';

export const Seasons: FC<SeasonsProps> = ({
  slug,
  isVerticalList = false,
  isRestricted = true,
}) => {
  const { data: seasons = [], isFetching } = useGetProgramSeasons(
    slug,
    isRestricted
  );

  const mountContents = (contents: ProgramSeason['contents']) =>
    contents.map(
      ({ program_type_trans, title, published_at, slug, watch, thumb }) => (
        <>
          <ContentProgressCard
            key={title}
            subtitle={title}
            description={formatDateToPTBRString(new Date(published_at))}
            progress={watch ? watch.percentage : 0}
            isEditable
            slug={`${PATHS.hubs.advisory}${PATHS.hubs.content}/${slug}`}
            thumbAlt={title}
            thumbUrl={thumb}
            title={program_type_trans}
            containerProps={
              isVerticalList
                ? { border: 'none', h: '0rem', minH: '160px', p: 's' }
                : { w: '21.75rem' }
            }
          />
          <IfRender condition={isVerticalList}>
            <Divider borderWidth="1px" borderColor="border.secondary" mb="s" />
          </IfRender>
        </>
      )
    );

  return (
    <>
      <IfRender condition={isFetching}>
        {isVerticalList ? (
          <Flex direction="column">
            <SkeletonCircle w="120px" h="40px" />
            {[...Array(3).keys()].map((idx) => (
              <Fragment key={idx}>
                <Skeleton w="21.75rem" h="180px" />
                <Divider
                  borderWidth="1px"
                  borderColor="border.secondary"
                  mb="s"
                />
              </Fragment>
            ))}
          </Flex>
        ) : (
          <Flex direction="column" gap="l">
            <SkeletonCircle w="120px" h="40px" />
            <Flex gap="l">
              {[...Array(3).keys()].map((idx) => (
                <Skeleton key={idx} w="280px" h="180px" />
              ))}
            </Flex>
          </Flex>
        )}
      </IfRender>

      <IfRender condition={!isFetching}>
        <Tabs display="flex" flexDir="column" variant="solid-rounded" gap="l">
          <TabList w="fit-content">
            {seasons.map(({ title }) => (
              <Tab key={title}>{title}</Tab>
            ))}
          </TabList>
          <TabPanels>
            {seasons.map(({ slug, title, contents }) => (
              <TabPanel
                key={`${title}-content`}
                p={0}
                sx={{
                  '& > div > div': {
                    _first: {
                      right: '2xl',
                      position: 'absolute',
                      top: '-7rem',
                    },
                    mr: 'negative.2xl',
                    minH: 0,
                  },
                  '& .swiper-slide': {
                    width: 'fit-content',
                    _last: { pr: '2xl' },
                  },
                }}
              >
                {isVerticalList ? (
                  <Flex direction="column" gap="l">
                    {mountContents(contents)}
                  </Flex>
                ) : (
                  <Slider
                    id={`slider-program-season-${slug.replaceAll(' ', '')}`}
                    displayProgress={false}
                    controlsAlignment="top-right"
                    swiperGap="0px"
                  >
                    {mountContents(contents)}
                  </Slider>
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </IfRender>
    </>
  );
};

type SeasonsProps = {
  slug: string;
  isVerticalList?: boolean;
  isRestricted?: boolean;
};
